
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  Users, Target, Download, Printer, 
  ChevronRight, X, FileText, UserCheck, RefreshCw,
  BarChart3, PieChart as PieChartIcon, Hash, Building2,
  Calendar, ArrowRight, Clock
} from 'lucide-react';
import { FilterBar } from '../features/reporting/FilterBar';
import { ReportCharts } from '../features/reporting/ReportCharts';
import { reportService, AuditRecord } from '../services/reportService';
import { ReportFilters } from '../types';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import jsPDF from 'jspdf';
import { toPng } from 'html-to-image';

import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import { AppSettings } from '../types';

const ReportsPage: React.FC = () => {
  const { profile } = useAuth();
  const { data: settings } = useQuery({
    queryKey: ['appSettingsReports'],
    queryFn: async () => {
      const { data } = await supabase.from('app_settings').select('*').single();
      return data as AppSettings;
    }
  });

  const DEFAULT_FILTERS = React.useMemo<ReportFilters>(() => ({
    startDate: '2026-01-01',
    endDate: '2026-12-31',
    programIds: [],
    audienceIds: []
  }), []);

  const [filters, setFilters] = React.useState<ReportFilters>(() => {
    const val = localStorage.getItem('reports_filters');
    if (val === null) return {
      startDate: '2026-01-01',
      endDate: '2026-12-31',
      programIds: [],
      audienceIds: []
    };
    try { 
      return JSON.parse(val); 
    } catch { 
      return {
        startDate: '2026-01-01',
        endDate: '2026-12-31',
        programIds: [],
        audienceIds: []
      }; 
    }
  });

  React.useEffect(() => {
    localStorage.setItem('reports_filters', JSON.stringify(filters));
  }, [filters]);

  const [auditModal, setAuditModal] = React.useState<{
    type: 'registered' | 'headcount' | 'staff' | 'engagements' | 'volunteers' | 'coaching_hours' | 'instructional_hours';
    title: string;
  } | null>(null);

  const [isPrinting, setIsPrinting] = React.useState(false);
  const reportRef = React.useRef<HTMLDivElement>(null);

  const handlePrint = async () => {
    if (!reportRef.current) return;
    setIsPrinting(true);
    
    try {
      // Add capturing class to show PDF-only elements
      reportRef.current.classList.add('is-capturing');
      
      // Small delay to ensure any hover states or transitions are settled
      await new Promise(resolve => setTimeout(resolve, 500));

      // Capture the report container as a PNG
      const imgData = await toPng(reportRef.current, {
        quality: 1,
        pixelRatio: 2,
        backgroundColor: '#f8fafc',
        width: 1200,
        cacheBust: true,
        imagePlaceholder: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=', // Transparent 1x1 pixel
        style: {
          transform: 'scale(1)',
          transformOrigin: 'top left',
          width: '1200px',
          padding: '40px',
          boxSizing: 'border-box'
        },
        // Filter out any elements that shouldn't be in the PDF
        filter: (node) => {
          if (node instanceof HTMLElement) {
            return !node.classList.contains('print:hidden');
          }
          return true;
        }
      });

      // Remove capturing class immediately after capture
      reportRef.current.classList.remove('is-capturing');

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'px',
        format: 'a4',
        hotfixes: ['px_scaling']
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      const imgProps = pdf.getImageProperties(imgData);
      const ratio = imgProps.width / imgProps.height;
      const displayWidth = pdfWidth;
      const displayHeight = pdfWidth / ratio;

      // Handle multi-page PDF if content is longer than one A4 page
      let heightLeft = displayHeight;
      let position = 0;

      // Add the first page
      pdf.addImage(imgData, 'PNG', 0, position, displayWidth, displayHeight);
      heightLeft -= pdfHeight;

      // Add additional pages if needed
      while (heightLeft > 0) {
        position = heightLeft - displayHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, displayWidth, displayHeight);
        heightLeft -= pdfHeight;
      }

      pdf.save(`HFLI_Impact_Report_${filters.startDate}_to_${filters.endDate}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      // Ensure class is removed even on error
      if (reportRef.current) {
        reportRef.current.classList.remove('is-capturing');
      }
    } finally {
      setIsPrinting(false);
    }
  };

  const { data: report, isLoading, isError, refetch } = useQuery({
    queryKey: ['impact-report', filters],
    queryFn: () => reportService.getImpactReport(filters)
  });

  const { data: auditData, isLoading: isAuditLoading } = useQuery({
    queryKey: ['impact-audit', auditModal?.type, filters],
    queryFn: () => auditModal ? reportService.getImpactAudit(auditModal.type, filters) : Promise.resolve([]),
    enabled: !!auditModal
  });

  const handleFilterChange = (newFilters: Partial<ReportFilters>) => {
    setFilters((prev: ReportFilters) => ({ ...prev, ...newFilters }));
  };

  const handleClearFilters = () => {
    setFilters({ 
      startDate: '2026-01-01', 
      endDate: '2026-12-31', 
      programIds: [],
      audienceIds: []
    });
  };

  const exportToCSV = (data: any[], filename: string) => {
    if (!data || data.length === 0) return;
    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(','),
      ...data.map(row => headers.map(h => `"${row[h] || ''}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', `${filename}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const summary = report?.summary || { total_served: 0, registered: 0, headcount: 0, engagements: 0, staff: 0, volunteers: 0, volunteer_hours: 0, coaching_hours: 0 };

  return (
    <div className="flex flex-col gap-8 pb-20">
      <style dangerouslySetInnerHTML={{ __html: `
        @media print {
          body { background: white !important; }
          aside { display: none !important; }
          header { display: none !important; }
          main { padding: 0 !important; margin: 0 !important; overflow: visible !important; }
          .pb-20 { padding-bottom: 0 !important; }
          .gap-8 { gap: 2rem !important; }
          .rounded-xl { border-radius: 0.5rem !important; }
          .shadow-sm { box-shadow: none !important; }
          .print\:hidden { display: none !important; }
          .print\:block { display: block !important; }
        }

        /* PDF capture fix - ensure standard colors for better compatibility */
        .is-capturing.pdf-capture {
          padding: 40px !important;
          background-color: #f8fafc !important;
          color: #0f172a !important; /* slate-900 */
          box-sizing: border-box !important;
          width: 1200px !important;
        }
        
        .is-capturing.pdf-capture * {
          border-color: rgba(226, 232, 240, 1) !important; /* slate-200 */
        }

        .is-capturing.pdf-capture .bg-slate-50 { background-color: #f8fafc !important; }
        .is-capturing.pdf-capture .bg-slate-800 { background-color: #1e293b !important; }
        .is-capturing.pdf-capture .bg-slate-900 { background-color: #0f172a !important; }
        .is-capturing.pdf-capture .text-slate-400 { color: #94a3b8 !important; }
        .is-capturing.pdf-capture .text-slate-900 { color: #0f172a !important; }
        .is-capturing.pdf-capture .text-white\/40 { color: rgba(255, 255, 255, 0.4) !important; }
        .is-capturing.pdf-capture .text-white\/20 { color: rgba(255, 255, 255, 0.2) !important; }
        
        .pdf-header {
          display: none !important;
        }
        
        .pdf-capture.is-capturing .pdf-header {
          display: flex !important;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 40px;
          padding-bottom: 20px;
          border-bottom: 2px solid #0f172a;
          width: 100%;
        }
      `}} />
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-200 pb-8 print:hidden">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tighter uppercase leading-none">Reports</h1>
          <p className="text-[10px] font-black text-slate-400 mt-2 uppercase tracking-[0.3em]">Impact & Analytics Dashboard</p>
        </div>
        
        <div className="flex items-center gap-3">
          <Button 
            variant="outline" 
            className="border-slate-200 text-slate-600 text-[10px] font-black uppercase tracking-widest px-4 py-2 h-auto" 
            icon={isPrinting ? <RefreshCw size={14} className="animate-spin" /> : <Printer size={14} />}
            onClick={handlePrint}
            disabled={isPrinting || isLoading}
          >
            {isPrinting ? 'Generating...' : 'Print PDF'}
          </Button>
        </div>
      </div>

      {/* Report Content Container */}
      <div ref={reportRef} data-report-container className="flex flex-col gap-8 pdf-capture">
        <div className="pdf-header">
          <div className="flex items-center gap-4">
            {settings?.branding?.logo_url ? (
              <div className="relative w-16 h-16">
                <img 
                  src={settings.branding.logo_url} 
                  className="w-16 h-16 object-contain" 
                  alt="Logo" 
                  referrerPolicy="no-referrer"
                  // Removed crossOrigin="anonymous" to avoid console errors on page load.
                  // Note: External logos without CORS won't appear in the PDF, fallback will be used.
                  onError={(e) => {
                    // If image fails to load (e.g. CORS), hide it and show placeholder
                    const img = e.target as HTMLImageElement;
                    img.style.display = 'none';
                    const fallback = img.nextElementSibling as HTMLElement;
                    if (fallback) fallback.style.display = 'flex';
                  }}
                />
                <div className="hidden w-16 h-16 bg-[#FFDB00] rounded-2xl items-center justify-center text-black shadow-lg">
                  <Building2 size={32} />
                </div>
              </div>
            ) : (
              <div className="w-16 h-16 bg-[#FFDB00] rounded-2xl flex items-center justify-center text-black shadow-lg">
                <Building2 size={32} />
              </div>
            )}
            <div>
              <h2 className="text-3xl font-black text-slate-900 uppercase tracking-tighter leading-none">Impact Report</h2>
              <p className="text-[10px] font-black text-slate-400 mt-2 uppercase tracking-[0.3em]">Henry Ford Learning Institute</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Report Period</p>
            <p className="text-sm font-black text-slate-900 uppercase tracking-tight">
              {filters.startDate} — {filters.endDate}
            </p>
          </div>
        </div>

        {/* Filter Bar */}
        <div>
          <FilterBar 
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            isLoading={isLoading}
          />
        </div>

        {/* Row 1: Individual Served (Light) */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          <div className="p-6 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-600 text-white border border-slate-200 rounded-lg">
                <Users size={20} />
              </div>
              <h2 className="text-xl font-black text-slate-900 uppercase tracking-tighter">Individual Served</h2>
            </div>
            <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
              Unique Reach Breakdown
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-12 divide-y md:divide-y-0 md:divide-x divide-slate-200">
            {/* Left: Totals */}
            <div className="md:col-span-5 p-8 flex flex-col justify-center">
              <div 
                className="group cursor-pointer"
                onClick={() => setAuditModal({ type: 'registered', title: 'Total Individuals Served' })}
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Grand Total</span>
                  <ChevronRight size={12} className="text-slate-300 group-hover:text-blue-600 transition-colors" />
                </div>
                <h3 className="text-7xl font-black text-slate-900 tracking-tighter leading-none mb-8 group-hover:text-blue-600 transition-colors">
                  {isLoading ? '...' : summary.total_served}
                </h3>
              </div>

              <div className="grid grid-cols-2 gap-8 pt-8 border-t-2 border-slate-100">
                <div 
                  className="space-y-1 group cursor-pointer"
                  onClick={() => setAuditModal({ type: 'registered', title: 'Registered Participants' })}
                >
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1">
                    Registered <ChevronRight size={10} className="text-slate-300 group-hover:text-blue-600" />
                  </p>
                  <p className="text-3xl font-black text-slate-900 tracking-tight group-hover:text-blue-600 transition-colors">
                    {isLoading ? '...' : summary.registered}
                  </p>
                  <p className="text-[9px] font-bold text-slate-400 uppercase">Rostered Personnel</p>
                </div>
                <div 
                  className="space-y-1 group cursor-pointer"
                  onClick={() => setAuditModal({ type: 'headcount', title: 'Headcount Estimates' })}
                >
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1">
                    Headcount <ChevronRight size={10} className="text-slate-300 group-hover:text-blue-600" />
                  </p>
                  <p className="text-3xl font-black text-slate-900 tracking-tight group-hover:text-blue-600 transition-colors">
                    {isLoading ? '...' : summary.headcount}
                  </p>
                  <p className="text-[9px] font-bold text-slate-400 uppercase">Estimated Presence</p>
                </div>
              </div>
            </div>

            {/* Right: Audience Breakdown */}
            <div className="md:col-span-7 p-8 bg-slate-50/50">
              <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-6">Audience Distribution</h4>
              <div className="space-y-4">
                {report?.audience_breakdown && report.audience_breakdown.length > 0 ? (
                  report.audience_breakdown.map((b, i) => (
                    <div key={i} className="space-y-2">
                      <div className="flex items-center justify-between text-[10px] font-black uppercase tracking-tight">
                        <span className="text-slate-900">{b.label}</span>
                        <span className="text-slate-400">{b.total} Served</span>
                      </div>
                      <div className="h-3 bg-white border border-slate-200 rounded-full overflow-hidden flex">
                        <div 
                          className="h-full bg-blue-600 border-r border-slate-200" 
                          style={{ width: `${(b.registered / (b.total || 1)) * 100}%` }} 
                          title={`Registered: ${b.registered}`}
                        />
                        <div 
                          className="h-full bg-blue-300" 
                          style={{ width: `${(b.headcount / (b.total || 1)) * 100}%` }} 
                          title={`Headcount: ${b.headcount}`}
                        />
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="py-12 text-center border-4 border-dashed border-slate-200">
                    <p className="text-[10px] font-bold uppercase text-slate-300 tracking-widest italic">No audience data found</p>
                  </div>
                )}
              </div>
              <div className="mt-6 flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-blue-600 border border-slate-200 rounded-sm" />
                  <span className="text-[9px] font-black uppercase text-slate-400">Registered</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-blue-300 border border-slate-200 rounded-sm" />
                  <span className="text-[9px] font-black uppercase text-slate-400">Headcount</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Row: Contact Summary (Light) */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          <div className="p-6 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-slate-900 text-[#FFDB00] rounded-lg">
                <Clock size={20} />
              </div>
              <h2 className="text-xl font-black text-slate-900 uppercase tracking-tighter">Contact Summary</h2>
            </div>
            <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
              Aggregate Delivery Hours
            </div>
          </div>
          
          <div className="p-8 grid grid-cols-1 md:grid-cols-3 gap-8 md:divide-x md:divide-slate-200">
            {/* Instructional Summary Card */}
            <div 
              className="group cursor-pointer space-y-2 pb-6 md:pb-0"
              onClick={() => setAuditModal({ type: 'instructional_hours', title: 'Instructional Hours Details' })}
              title="View detailed audit of all instructional session hours."
            >
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1">
                Instructional Summary <ChevronRight size={10} className="text-slate-300 group-hover:text-blue-600 transition-colors" />
              </p>
              <h3 className="text-5xl font-black text-blue-600 tracking-tighter group-hover:text-blue-600 transition-colors">
                {isLoading ? '...' : (summary as any).instructional_hours || 0}
              </h3>
              <p className="text-[9px] font-semibold text-slate-500 normal-case leading-snug">
                Delivered across all instructional sessions in selected period.
              </p>
            </div>

            {/* Coaching Summary Card */}
            <div 
              className="group cursor-pointer space-y-2 md:pl-8 pb-6 md:pb-0"
              onClick={() => setAuditModal({ type: 'coaching_hours', title: 'Coaching Hours Details' })}
              title="View detailed audit of all coaching session hours."
            >
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1">
                Coaching Summary <ChevronRight size={10} className="text-slate-300 group-hover:text-teal-600 transition-colors" />
              </p>
              <h3 className="text-5xl font-black text-teal-600 tracking-tighter group-hover:text-teal-600 transition-colors">
                {isLoading ? '...' : (summary.coaching_hours || 0)}
              </h3>
              <p className="text-[9px] font-semibold text-slate-500 normal-case leading-snug">
                Delivered across all coaching sessions in selected period.
              </p>
            </div>

            {/* Total Hours Card */}
            <div className="space-y-2 md:pl-8">
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                Total Hours
              </p>
              <h3 className="text-5xl font-black text-slate-900 tracking-tighter">
                {isLoading ? '...' : (((summary as any).instructional_hours || 0) + (summary.coaching_hours || 0))}
              </h3>
              <p className="text-[9px] font-bold text-slate-400 uppercase">Combined Operational Hours</p>
            </div>
          </div>
        </div>

        {/* Row 2: Engagement & Activity (Darker) */}
        <div className="bg-slate-800 border border-slate-200 rounded-xl shadow-sm overflow-hidden text-white">
          <div className="p-6 border-b border-slate-200 bg-slate-900 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-600 text-white border border-slate-200 rounded-lg">
                <Target size={20} />
              </div>
              <h2 className="text-xl font-black text-white uppercase tracking-tighter">Engagement & Activity</h2>
            </div>
            <div className="text-[10px] font-black text-white/40 uppercase tracking-widest">
              Organizational Output
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 divide-y md:divide-y-0 md:divide-x divide-slate-200">
            {/* Left: Activity Summary */}
            <div className="md:col-span-3 p-8 flex flex-col justify-center space-y-12">
              <div 
                className="group cursor-pointer"
                onClick={() => setAuditModal({ type: 'engagements', title: 'Concluded Engagements' })}
              >
                <p className="text-[10px] font-black text-white/40 uppercase tracking-widest mb-2 flex items-center gap-1">
                  Concluded <ChevronRight size={10} className="text-white/20 group-hover:text-emerald-400" />
                </p>
                <h3 className="text-6xl font-black text-white tracking-tighter leading-none group-hover:text-emerald-400 transition-colors">
                  {isLoading ? '...' : summary.engagements}
                </h3>
                <p className="text-[9px] font-bold text-white/40 uppercase mt-2">Strategic Initiatives</p>
              </div>

              <div className="grid grid-cols-1 gap-8 mt-auto pt-8 border-t border-white/10">
                <div 
                  className="group cursor-pointer"
                  onClick={() => setAuditModal({ type: 'staff', title: 'Staff Personnel' })}
                >
                  <p className="text-[10px] font-black text-white/40 uppercase tracking-widest mb-2 flex items-center gap-1">
                    Staff <ChevronRight size={10} className="text-white/20 group-hover:text-emerald-400" />
                  </p>
                  <p className="text-3xl font-black text-white tracking-tight group-hover:text-emerald-400 transition-colors">
                    {isLoading ? '...' : summary.staff}
                  </p>
                  <p className="text-[9px] font-bold text-white/40 uppercase mt-1">Unique Personnel</p>
                </div>
              </div>
            </div>

            {/* Middle: Density Chart */}
            <div className="md:col-span-5 p-8 bg-slate-900/50">
              <h4 className="text-[10px] font-black text-white/40 uppercase tracking-widest mb-6">Session Density</h4>
              <div className="h-[240px]">
                <ReportCharts 
                  type="line" 
                  title="" 
                  unit="Sessions"
                  data={report?.monthly_trend.map(t => ({ name: t.month, value: t.count })) || []} 
                  isLoading={isLoading}
                  isDark={true}
                />
              </div>
            </div>

            {/* Right: Program Mix */}
            <div className="md:col-span-4 p-8">
              <h4 className="text-[10px] font-black text-white/40 uppercase tracking-widest mb-6 px-1">Program Mix</h4>
              <div className="h-[320px]">
                <ReportCharts 
                  type="pie" 
                  title="" 
                  unit="Engagements"
                  data={report?.program_mix?.map(p => ({ name: p.name, value: p.count })) || []} 
                  isLoading={isLoading}
                  isDark={true}
                />
              </div>
            </div>

            {/* VOLUNTEERING FULL SECTION */}
            <div className="col-span-1 md:col-span-12 border-t border-white/5 bg-slate-900/30 p-8">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                {/* Metrics */}
                <div className="lg:col-span-4 grid grid-cols-2 gap-4 lg:flex lg:flex-col lg:gap-8 lg:divide-y lg:divide-white/5 bg-white/2 rounded-xl p-8">
                  <div 
                    className="text-center lg:text-left group cursor-pointer lg:pb-8" 
                    onClick={() => setAuditModal({ type: 'volunteers', title: 'Volunteer Personnel' })}
                  >
                    <p className="text-[10px] font-black text-white/40 uppercase tracking-widest mb-4 flex items-center lg:justify-start justify-center gap-1">
                      Individuals <ChevronRight size={10} className="text-white/20 group-hover:text-emerald-400" />
                    </p>
                    <p className="text-6xl font-black text-white tracking-tighter leading-none group-hover:text-emerald-400 transition-colors">
                      {isLoading ? '...' : summary.volunteers}
                    </p>
                    <p className="text-[9px] font-bold text-white/40 uppercase mt-2">Engaged Contributors</p>
                  </div>
                  
                  <div className="text-center lg:text-left pt-8">
                    <p className="text-[10px] font-black text-emerald-400/60 uppercase tracking-widest mb-4">Service Volume</p>
                    <p className="text-6xl font-black text-emerald-400 tracking-tighter leading-none">
                      {isLoading ? '...' : (summary.volunteer_hours || 0).toLocaleString()}
                    </p>
                    <p className="text-[9px] font-bold text-white/40 uppercase mt-2">Total Combined Hours</p>
                  </div>
                </div>
                
                {/* Distribution Chart */}
                <div className="lg:col-span-8 flex flex-col h-full min-h-[400px]">
                  <div className="flex items-center justify-between mb-8 px-2 border-b border-white/5 pb-4">
                    <div>
                      <h4 className="text-[11px] font-black text-white/60 uppercase tracking-[0.2em]">Volunteer Mix</h4>
                      <p className="text-[9px] font-bold text-white/20 uppercase mt-1">Activity distribution by volunteer type</p>
                    </div>
                    <div className="bg-emerald-500/10 px-3 py-1 rounded-full shrink-0">
                      <span className="text-[9px] font-black text-emerald-400 uppercase tracking-widest">Live Pulse</span>
                    </div>
                  </div>
                  <div className="flex-1 min-h-[300px]">
                    <ReportCharts 
                      type="pie" 
                      title="" 
                      unit="Contributions"
                      data={report?.volunteer_mix?.map(v => ({ name: v.name, value: v.count })) || []} 
                      isLoading={isLoading}
                      isDark={true}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Row 3: Regional Reach (New Section) */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          <div className="p-6 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#FFDB00] text-black border border-slate-200 rounded-lg">
                <Building2 size={20} />
              </div>
              <h2 className="text-xl font-black text-slate-900 uppercase tracking-tighter">Regional Reach</h2>
            </div>
            <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
              Geographic Service gaps
            </div>
          </div>
          
          <div className="p-8">
            {report?.regional_reach && report.regional_reach.length > 0 ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                {/* Horizontal Chart for Regions */}
                <div className="space-y-6">
                  <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Individuals served by session region</h4>
                  <div className="space-y-4">
                    {report.regional_reach.map((r, i) => (
                      <div key={i} className="space-y-2">
                        <div className="flex items-center justify-between text-[10px] font-black uppercase tracking-tight">
                          <span className="text-slate-900">{r.label}</span>
                          <span className="text-slate-400">{r.total} Served</span>
                        </div>
                        <div className="h-3 bg-slate-100 border border-slate-200 rounded-full overflow-hidden flex">
                          <div 
                            className="h-full bg-[#FFDB00] border-r border-slate-200" 
                            style={{ width: `${(r.registered / (Math.max(...report.regional_reach!.map(reg => reg.total)) || 1)) * 100}%` }} 
                          />
                          <div 
                            className="h-full bg-slate-200" 
                            style={{ width: `${(r.headcount / (Math.max(...report.regional_reach!.map(reg => reg.total)) || 1)) * 100}%` }} 
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Regional Stats Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-6">
                  {report.regional_reach.map((r, i) => (
                    <div key={i} className="bg-slate-50 border border-slate-200 p-4 rounded-lg flex flex-col justify-between">
                      <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest leading-tight mb-2 truncate" title={r.label}>
                        {r.label}
                      </p>
                      <div>
                        <p className="text-2xl font-black text-slate-900 leading-none">{r.total}</p>
                        <p className="text-[8px] font-bold text-slate-400 uppercase mt-1">Total Impact</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="py-12 text-center border-4 border-dashed border-slate-100">
                <p className="text-[10px] font-bold uppercase text-slate-300 tracking-widest italic">No regional data identified in session logs</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Audit Modal */}
      <Modal
        isOpen={!!auditModal}
        onClose={() => setAuditModal(null)}
        title={auditModal?.title || 'Audit Log'}
      >
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
              Showing raw records for selected metric
            </div>
            <Button 
              variant="outline" 
              className="border-slate-900 text-slate-900 text-[10px] font-black uppercase tracking-widest"
              icon={<Download size={14} />}
              onClick={() => exportToCSV(auditData || [], `audit_${auditModal?.type}`)}
              disabled={!auditData?.length}
            >
              Download CSV
            </Button>
          </div>

          <div className="border-4 border-slate-900 overflow-hidden">
            <table className="w-full text-left border-collapse">
              <thead className="bg-slate-900 text-white">
                <tr>
                  <th className="px-4 py-3 text-[10px] font-black uppercase tracking-widest border-r border-white/10">
                    {auditModal?.type === 'coaching_hours' || auditModal?.type === 'instructional_hours'
                      ? 'session'
                      : auditModal?.type === 'volunteers' ? 'Volunteer Name' : 'Name/Label'}
                  </th>
                  <th className="px-4 py-3 text-[10px] font-black uppercase tracking-widest border-r border-white/10">
                    {auditModal?.type === 'coaching_hours' || auditModal?.type === 'instructional_hours'
                      ? 'engagement'
                      : auditModal?.type === 'volunteers' ? 'Engagement (Type)' : 'Detail/Org'}
                  </th>
                  <th className="px-4 py-3 text-[10px] font-black uppercase tracking-widest border-r border-white/10">date</th>
                  <th className="px-4 py-3 text-[10px] font-black uppercase tracking-widest">
                    {auditModal?.type === 'coaching_hours' || auditModal?.type === 'instructional_hours'
                      ? 'logged hours'
                      : auditModal?.type === 'volunteers' ? 'Count' : 'Category/Count'}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y-2 divide-slate-900">
                {isAuditLoading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i}>
                      <td colSpan={4} className="px-4 py-3"><div className="h-4 bg-slate-100 animate-pulse" /></td>
                    </tr>
                  ))
                ) : auditData?.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-12 text-center text-slate-400 font-black uppercase tracking-widest italic">No records found</td>
                  </tr>
                ) : (
                  auditData?.map((row, i) => {
                    const isHourly = auditModal?.type === 'coaching_hours' || auditModal?.type === 'instructional_hours';
                    const cleanedName = isHourly
                      ? (row.name || '').replace(/^(Coaching Session|Instructional Session|Coaching|Instructional):\s*/i, '').trim()
                      : row.name;
                    return (
                      <tr key={i} className="hover:bg-slate-50 transition-colors">
                        <td className="px-4 py-3 text-xs font-black text-slate-900 border-r-2 border-slate-900">{cleanedName}</td>
                        <td className="px-4 py-3 text-xs font-bold text-slate-500 border-r-2 border-slate-900">{row.detail}</td>
                        <td className="px-4 py-3 text-xs font-mono text-slate-400 border-r-2 border-slate-900">{row.date}</td>
                        <td className="px-4 py-3 text-xs font-black text-slate-900">{row.category}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ReportsPage;
;
